import { differenceInDays, format, isToday } from 'date-fns'
import { pl } from 'date-fns/locale'

const UTC_DATE_WITH_SPACE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/

const toUTC = (date: Date | string): Date => {
	// Daty z API są w typie string
	if (typeof date === 'string') {
		const normalized = UTC_DATE_WITH_SPACE.test(date) ? `${date.replace(' ', 'T')}Z` : date
		return new Date(normalized)
	}
	// Lokalne daty np - new Date() pomija
	return date
}

export const parseToDate = ({ day, month, year }: { day: number; month: number; year: number }): Date =>
	new Date(year, month - 1, day)

export const formatTime = (date: Date | string): string => format(toUTC(date), 'HH:mm', { locale: pl })

export const formatDatePl = (date: Date | string): string => format(toUTC(date), 'dd MMM yyyy HH:mm', { locale: pl })

export const formatDate = (date: Date | string): string => format(toUTC(date), 'dd.MM.yyyy', { locale: pl })

export const formatDateAndTime = (date: Date | string): string =>
	format(toUTC(date), 'HH:mm dd.MM.yyyy', { locale: pl })

export const dateToDayMonth = (date: Date | string): string => format(toUTC(date), 'dd.MM', { locale: pl })

export const dateToDayMonthYear = (date: Date | string): string => format(toUTC(date), 'dd.MM.yyyy', { locale: pl })

export const dateToWeekdayShort = (date: Date | string): string => format(toUTC(date), 'EEE', { locale: pl })

export const dateToWeekday = (date: Date | string): string => format(toUTC(date), 'EEEE', { locale: pl })

export const dateToFilterFormat = (date: Date | string): string => format(toUTC(date), 'yyyy-MM-dd', { locale: pl })

export const formatDateToInput = (date: Date | string): string => format(toUTC(date), 'MM/dd/yyyy', { locale: pl })

export const formatDateToMonthName = (date: Date | string): string => format(toUTC(date), 'MMMM', { locale: pl })

export const formatDateToNameOrDate = (date: Date | string): string => {
	const utcDate = toUTC(date)
	if (isToday(utcDate)) {
		return 'Dzisiaj'
	}
	return formatDate(utcDate)
}

export const formatDateToNameOrDateWithTime = (date: Date | string): string => {
	const utcDate = toUTC(date)
	if (isToday(utcDate)) {
		return `Dzisiaj ${formatTime(utcDate)}`
	}
	return formatDate(utcDate)
}

export const generateWeekDatesFromDate = (date: Date): Date[] => {
	const dayOfWeek = date.getDay() === 0 ? DAYS_IN_WEEK : date.getDay()
	const firstDayOfWeek = new Date(date)
	firstDayOfWeek.setDate(date.getDate() - (dayOfWeek - 1))

	const week = []
	for (let i = 0; i < DAYS_IN_WEEK; i++) {
		const currentDate = new Date(firstDayOfWeek)
		currentDate.setDate(firstDayOfWeek.getDate() + i)
		week.push(currentDate)
	}
	return week
}

export const generateMonthsOfYear = (year?: number): Array<string> => {
	const currentYear = year ?? new Date().getFullYear()
	const months = []
	for (let i = 0; i < MONTHS_IN_YEAR; i++) {
		const date = new Date(currentYear, i, 1)
		months.push(formatDateToMonthName(date))
	}
	return months
}

export const generateDatesFromRange = (from: Date, to: Date): Date[] => {
	const numberOfDays = differenceInDays(to, from)
	const week = []
	for (let i = 0; i < numberOfDays; i++) {
		const date = new Date(from)
		date.setDate(from.getDate() + i)
		week.push(date)
	}
	return week
}

export const formatDateForPayload = (date: Date | string): Date => {
	const parsedDate = typeof date === 'string' ? new Date(date) : date
	parsedDate.setHours(23, 59, 59, 999)
	return parsedDate
}

const DATE_FORMAT_YEAR_TOKEN = /YYYY/g
const DATE_FORMAT_DAY_TOKEN = /DD/g

const toDateFnsFormat = (outputFormat: string): string =>
	outputFormat.replace(DATE_FORMAT_YEAR_TOKEN, 'yyyy').replace(DATE_FORMAT_DAY_TOKEN, 'dd')

export const formatDateForFilterOutput = (date: Date | string, outputFormat?: string): string => {
	if (!outputFormat) {
		return formatDateForPayload(date).toISOString()
	}

	return format(toUTC(date), toDateFnsFormat(outputFormat), { locale: pl })
}
